/* ==========================================================================
   <tint-filters> — filter shelf behaviour
   --------------------------------------------------------------------------
   The shelf is a plain <form method="get">. Everything below is enhancement:
   with this file removed the form still submits and Shopify still filters.

   What it adds
     • Instant apply. Ticking a box or changing the sort fetches the filtered
       page and swaps in the new product grid and the shelf itself, without a
       full navigation.
     • History. Each change pushes a real URL, so back/forward work and a
       filtered view can be linked or bookmarked.
     • Focus care. After a swap, focus returns to the control that was used,
       and the result count is announced through the existing aria-live region.
     • Panels close on outside click and on Escape, and only one is open at a
       time — a shelf with six open dropdowns is not a shelf.
   ========================================================================== */

const GRID_SELECTORS = [
  '[data-tt-results]',
  '.collection-wrapper',
  '#main-collection-product-grid',
  '.product-grid-container',
  'main .shopify-section:has(.product-grid)',
];

class TintFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-form]');
    if (!this.form) return;

    this.auto = this.dataset.autoApply === 'true';
    this.controller = null;

    this.form.addEventListener('input', this.onInput);
    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('submit', this.onSubmit);

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('popstate', this.onPopState);
    this.controller?.abort();
  }

  /* ------------------------------------------------------------- handlers */

  onChange = (event) => {
    if (!this.auto) return;
    const target = event.target;
    if (target.matches('[data-sort]') || target.matches('input[type="checkbox"]')) {
      this.apply(target);
    }
  };

  onInput = (event) => {
    // Price fields fire on every keystroke; wait for the typing to settle.
    if (!this.auto) return;
    if (!event.target.matches('input[type="number"]')) return;
    window.clearTimeout(this.priceTimer);
    this.priceTimer = window.setTimeout(() => this.apply(event.target), 550);
  };

  onSubmit = (event) => {
    event.preventDefault();
    this.apply(document.activeElement);
  };

  onDocumentClick = (event) => {
    const openGroups = this.querySelectorAll('[data-group][open]');
    if (openGroups.length === 0) return;
    for (const group of openGroups) {
      if (!group.contains(event.target)) group.removeAttribute('open');
    }
  };

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('[data-group][open]');
    if (!open) return;
    open.removeAttribute('open');
    open.querySelector('summary')?.focus();
  };

  onPopState = () => {
    this.fetchInto(window.location.href, null, false);
  };

  /* ---------------------------------------------------------------- apply */

  apply(originator) {
    const params = new URLSearchParams(new FormData(this.form));
    // FormData keeps empty price inputs; drop them so the URL stays clean.
    for (const [key, value] of Array.from(params.entries())) {
      if (value === '') params.delete(key);
    }
    const base = this.form.getAttribute('action') || window.location.pathname;
    const query = params.toString();
    const url = query ? `${base}?${query}` : base;
    this.fetchInto(url, originator, true);
  }

  async fetchInto(url, originator, push) {
    const shelf = this.closest('.tt-shelf');
    shelf?.classList.add('is-busy');

    this.controller?.abort();
    this.controller = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.controller.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.swapResults(html);
      this.swapShelf(html, originator);

      if (push) window.history.pushState({ tintFilters: true }, '', url);
      document.dispatchEvent(new CustomEvent('tint:refresh'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Anything unexpected: hand the job back to the browser rather than
      // leaving the shopper looking at a stale grid.
      window.location.assign(url);
    } finally {
      shelf?.classList.remove('is-busy');
    }
  }

  swapResults(doc) {
    for (const selector of GRID_SELECTORS) {
      let incoming;
      try {
        incoming = doc.querySelector(selector);
      } catch (err) {
        continue; // :has() is unsupported in this browser — try the next one
      }
      if (!incoming) continue;

      let current;
      try {
        current = document.querySelector(selector);
      } catch (err) {
        continue;
      }
      if (!current) continue;

      current.replaceWith(incoming);
      return true;
    }
    return false;
  }

  swapShelf(doc, originator) {
    const incoming = doc.querySelector('tint-filters');
    if (!incoming) return;

    // Remember which panel was open and what had focus, so the shelf does
    // not collapse under the shopper mid-decision.
    const openIndex = Array.from(this.querySelectorAll('[data-group]'))
      .findIndex((group) => group.hasAttribute('open'));
    const focusName = originator?.getAttribute?.('name') || null;
    const focusValue = originator?.getAttribute?.('value') || null;

    this.innerHTML = incoming.innerHTML;
    this.form = this.querySelector('[data-form]');
    this.form?.addEventListener('input', this.onInput);
    this.form?.addEventListener('change', this.onChange);
    this.form?.addEventListener('submit', this.onSubmit);

    if (openIndex > -1) {
      this.querySelectorAll('[data-group]')[openIndex]?.setAttribute('open', '');
    }

    if (focusName) {
      const selector = focusValue
        ? `[name="${CSS.escape(focusName)}"][value="${CSS.escape(focusValue)}"]`
        : `[name="${CSS.escape(focusName)}"]`;
      this.querySelector(selector)?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('tint-filters')) {
  customElements.define('tint-filters', TintFilters);
}
